import { watch } from 'vue'
import { ApiError } from '@fusapp-work/api'
import type { QueryClient } from '@fusapp-work/shell'
import { useCurrentSession } from './useCurrentSession'

/** Install once on the Console bootstrap's existing client, before session resolution. */
export function installSessionCacheLifecycle(cache: QueryClient, navigateToLogin: () => Promise<unknown>): () => void {
  const session = useCurrentSession()
  const queries = cache.getQueryCache()
  const mutations = cache.getMutationCache()
  const stop = watch(() => session.state.status === 'authenticated' && session.state.session
    ? JSON.stringify([session.state.session.userId, [...session.state.session.roles].sort()]) : null, () => {
    // Cancellation is synchronous; clearing also removes retained mutation results.
    // A late result belongs to the removed query, never the next principal's cache.
    void cache.cancelQueries()
    cache.clear()
  }, { flush: 'sync' })

  function expire(error: unknown) {
    if (session.state.status !== 'authenticated' || !(error instanceof ApiError)
      || (error.status !== 401 && error.resultStatus !== 401)) return
    session.expire()
    // A navigation failure must not restore a revoked session or its private cache.
    void navigateToLogin().catch(() => {})
  }
  const unsubscribeQueries = queries.subscribe(event => {
    if (event.type === 'updated' && (event.action.type === 'error' || event.action.type === 'failed')
      && queries.getAll().includes(event.query)) expire(event.action.error)
  })
  const unsubscribeMutations = mutations.subscribe(event => {
    if (event.type === 'updated' && (event.action.type === 'error' || event.action.type === 'failed')
      && mutations.getAll().includes(event.mutation)) expire(event.action.error)
  })
  return () => { stop(); unsubscribeQueries(); unsubscribeMutations() }
}
